import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Box,
  Button,
  TextField,
} from "@mui/material";
import { Check as CheckIcon, Close as CloseIcon, Business as BusinessIcon, Edit as EditIcon } from "@mui/icons-material";
import { db } from "../../../firebase-config";
import { useParams } from "react-router-dom";
import { doc, getDoc, updateDoc, deleteDoc, collection, getDocs } from "firebase/firestore";

const AdminCompanyDetails = () => {
  const { id: companyId } = useParams();
  const [company, setCompany] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingDomain, setEditingDomain] = useState(false);
  const [newDomain, setNewDomain] = useState("");

  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        const companyRef = doc(db, "companies", companyId);
        const companySnap = await getDoc(companyRef);

        if (!companySnap.exists()) {
          setError("Company not found.");
          setLoading(false);
          return;
        }

        setCompany({ id: companySnap.id, ...companySnap.data() });

        const quotesRef = collection(db, "quotes");
        const quotesSnapshot = await getDocs(quotesRef);
        const companyQuotes = quotesSnapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((quote) => quote.companyId === companyId);

        setQuotes(companyQuotes);
      } catch (err) {
        console.error("Error fetching company data:", err);
        setError("Failed to load company details.");
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyData();
  }, [companyId]);

  const handleApproveQuote = async (quoteId) => {
    try {
      const quoteRef = doc(db, "quotes", quoteId);
      await updateDoc(quoteRef, { status: "approved" });
      setQuotes((prev) =>
        prev.map((quote) => (quote.id === quoteId ? { ...quote, status: "approved" } : quote))
      );
    } catch (err) {
      console.error("Error approving quote:", err);
      setError("Failed to approve quote.");
    }
  };

  const handleRejectQuote = async (quoteId) => {
    try {
      await deleteDoc(doc(db, "quotes", quoteId));
      setQuotes((prev) => prev.filter((quote) => quote.id !== quoteId));
    } catch (err) {
      console.error("Error rejecting quote:", err);
      setError("Failed to reject quote.");
    }
  };

  const handleApproveCompany = async () => {
    try {
      const companyRef = doc(db, "companies", companyId);
      await updateDoc(companyRef, { approved: true });
      setCompany((prev) => ({ ...prev, approved: true }));
    } catch (err) {
      console.error("Error approving company:", err);
      setError("Failed to approve company.");
    }
  };

  const handleEditDomain = async () => {
    try {
      const companyRef = doc(db, "companies", companyId);
      await updateDoc(companyRef, { emailDomain: newDomain });
      setCompany((prev) => ({ ...prev, emailDomain: newDomain }));
      setEditingDomain(false);
    } catch (err) {
      console.error("Error updating domain:", err);
      setError("Failed to update domain.");
    }
  };

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Company Details
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <BusinessIcon sx={{ mr: 2 }} />
        <Typography variant="h5">{company.name}</Typography>
      </Box>

      <Typography variant="body1">Company ID: {company.id}</Typography>

      <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
        <Typography variant="body1" sx={{ mr: 2 }}>
          Email Domain: {editingDomain ? <TextField value={newDomain} onChange={(e) => setNewDomain(e.target.value)} /> : company.emailDomain}
        </Typography>
        {editingDomain ? (
          <Button variant="contained" onClick={handleEditDomain}>Save</Button>
        ) : (
          <IconButton onClick={() => setEditingDomain(true)}><EditIcon /></IconButton>
        )}
      </Box>

      <Box sx={{ mt: 2 }}>
        {company.approved ? (
          <Typography color="success">Approved</Typography>
        ) : (
          <Button variant="contained" color="primary" onClick={handleApproveCompany}>
            Approve Company
          </Button>
        )}
      </Box>

      <Divider sx={{ mt: 4, mb: 2 }} />

      <Typography variant="h5" gutterBottom>
        Quotes
      </Typography>

      {quotes.length === 0 ? (
        <Typography variant="body1">No quotes found.</Typography>
      ) : (
        <List>
          {quotes.map((quote, index) => (
            <React.Fragment key={quote.id}>
              <ListItem alignItems="flex-start">
                <ListItemText
                  primary={`Quote ID: ${quote.id}`}
                  secondary={
                    <>
                      <Typography variant="body2">
                        Status: {quote.status} | Created At: {new Date(quote.createdAt.toDate()).toLocaleDateString()}
                      </Typography>
                      <Typography variant="body2">
                        Items: {quote.items.length}
                      </Typography>
                    </>
                  }
                />
                <ListItemSecondaryAction>
                  {quote.status !== "approved" && (
                    <>
                      <IconButton color="success" onClick={() => handleApproveQuote(quote.id)}>
                        <CheckIcon />
                      </IconButton>
                      <IconButton color="error" onClick={() => handleRejectQuote(quote.id)}>
                        <CloseIcon />
                      </IconButton>
                    </>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
              {index !== quotes.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      )}
    </Container>
  );
};

export default AdminCompanyDetails;
